import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CallOutlinedIcon from '@mui/icons-material/CallOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridFooterContainer,
  GridPagination,
} from '@mui/x-data-grid';
import { formatDuration } from '../utils';
import {
  formatConversationDate,
  listAgentConversationsPage,
  updateAgentConversation,
} from '../services/agentConversationService';

const PAGE_SIZE = 25;

const statusLabel = (status) => {
  if (status === 'transcribed') return 'Ավարտված';
  if (status === 'processing') return 'Ընթացքում';
  if (status === 'uploading') return 'Վերբեռնվում է';
  return 'Խափանված';
};

const statusColor = (status) => {
  if (status === 'transcribed') return 'success';
  if (status === 'processing' || status === 'uploading') return 'warning';
  return 'error';
};

const normalizeSearchText = (conversation) => {
  const metadata = conversation?.metadata && typeof conversation.metadata === 'object'
    ? conversation.metadata
    : {};

  return [
    conversation?.title || '',
    metadata.callerNumber || '',
    metadata.dialedNumber || '',
    metadata.agentName || '',
  ].join(' ').toLowerCase();
};

const AgentConversationListView = ({
  agent,
  onBack,
  onEditAgent,
  onOpenConversation,
}) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursorId, setCursorId] = useState(null);
  const [query, setQuery] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    let isMounted = true;

    if (!agent?.id) {
      setConversations([]);
      setLoading(false);
      setInitialLoadComplete(false);
      setHasMore(false);
      setCursorId(null);
      return undefined;
    }

    const loadInitialPage = async () => {
      setLoading(true);
      setInitialLoadComplete(false);
      setConversations([]);
      setHasMore(false);
      setCursorId(null);
      setQuery('');

      try {
        const response = await listAgentConversationsPage({
          agentId: agent.id,
          pageSize: PAGE_SIZE,
        });

        if (!isMounted) {
          return;
        }

        setConversations(response.items);
        setCursorId(response.nextCursorId);
        setHasMore(response.hasMore);
      } catch (error) {
        console.error('Error loading agent conversations:', error);
        if (isMounted) {
          setSnackbar({
            open: true,
            message: 'Զրույցների ցանկը բեռնել չհաջողվեց։',
            severity: 'error',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
          setInitialLoadComplete(true);
        }
      }
    };

    loadInitialPage();

    return () => {
      isMounted = false;
    };
  }, [agent?.id]);

  const filteredConversations = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      return conversations;
    }

    return conversations.filter((conversation) => normalizeSearchText(conversation).includes(normalizedQuery));
  }, [conversations, query]);

  const handleToggleStar = useCallback(async (event, conversation) => {
    event.stopPropagation();
    const nextStarred = !conversation.starred;

    try {
      await updateAgentConversation({
        conversationId: conversation.id,
        updates: { starred: nextStarred },
      });

      setConversations((previous) => previous.map((item) => (
        item.id === conversation.id
          ? { ...item, starred: nextStarred }
          : item
      )));
    } catch (error) {
      console.error('Error toggling agent conversation star:', error);
      setSnackbar({
        open: true,
        message: 'Չհաջողվեց պահպանել աստղանիշը։',
        severity: 'error',
      });
    }
  }, []);

  const rows = useMemo(() => (
    filteredConversations.map((conversation) => {
      const metadata = conversation?.metadata && typeof conversation.metadata === 'object'
        ? conversation.metadata
        : {};

      return {
        ...conversation,
        callerNumber: metadata.callerNumber || '',
        dialedNumber: metadata.dialedNumber || '',
        titleText: conversation.title || metadata.callerNumber || 'Անվերնագիր զրույց',
        createdAtText: formatConversationDate(conversation.createdAtMs || conversation.createdAt),
      };
    })
  ), [filteredConversations]);

  const columnVisibilityModel = useMemo(() => (
    isXs
      ? {
        callerNumber: false,
        dialedNumber: false,
        duration: false,
        status: false,
      }
      : {}
  ), [isXs]);

  const columns = useMemo(() => ([
    {
      field: 'starred',
      headerName: '',
      width: 62,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: (params) => {
        const isStarred = Boolean(params.value);
        return (
          <Tooltip title={isStarred ? 'Հեռացնել աստղը' : 'Ավելացնել աստղ'}>
            <IconButton
              size="small"
              onClick={(event) => handleToggleStar(event, params.row)}
              sx={{
                color: isStarred ? 'warning.main' : 'text.disabled',
                '&:hover': { color: 'warning.main' },
              }}
            >
              {isStarred ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
        );
      },
    },
    {
      field: 'titleText',
      headerName: 'Զրույց',
      flex: 1.45,
      minWidth: isXs ? 220 : 280,
      sortComparator: (a, b) => String(a || '').localeCompare(String(b || ''), 'hy-AM'),
      renderCell: (params) => (
        <Box
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            overflow: 'hidden',
            py: isXs ? 0.5 : 0,
          }}
        >
          <Stack spacing={0.5} sx={{ width: '100%', minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                fontWeight: 600,
              }}
            >
              {params.row.titleText}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {params.row.callerNumber || 'Զանգահարողը նշված չէ'}
            </Typography>
            {isXs && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                  {formatDuration(Number(params.row.duration || 0))}
                </Typography>
                <Chip
                  size="small"
                  color={statusColor(params.row.status)}
                  label={statusLabel(params.row.status)}
                  sx={{ maxWidth: '100%' }}
                />
              </Stack>
            )}
          </Stack>
        </Box>
      ),
    },
    {
      field: 'callerNumber',
      headerName: 'Զանգահարող',
      minWidth: 170,
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'dialedNumber',
      headerName: 'Ընդունող համար',
      minWidth: 170,
      flex: 0.9,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'duration',
      headerName: 'Տևողություն',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Typography variant="body2">
          {formatDuration(Number(params.value || 0))}
        </Typography>
      ),
      sortComparator: (a, b) => Number(a || 0) - Number(b || 0),
    },
    {
      field: 'createdAtMs',
      headerName: 'Ամսաթիվ',
      width: isXs ? 150 : 190,
      renderCell: (params) => (
        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {params.row.createdAtText}
        </Box>
      ),
      sortComparator: (a, b) => Number(a || 0) - Number(b || 0),
    },
    {
      field: 'status',
      headerName: 'Կարգավիճակ',
      width: 140,
      renderCell: (params) => (
        <Chip
          size="small"
          color={statusColor(params.value)}
          label={statusLabel(params.value)}
          variant="outlined"
          sx={{ fontWeight: 500 }}
        />
      ),
    },
  ]), [handleToggleStar, isXs]);

  const localeText = useMemo(() => ({
    noRowsLabel: 'Տվյալներ չկան',
    MuiTablePagination: {
      labelRowsPerPage: 'Տողեր յուրաքանչյուր էջում:',
      labelDisplayedRows: ({ from, to, count }) =>
        `${from}–${to} / ${count !== -1 ? count : `ավելին քան ${to}`}`,
    },
  }), []);

  const handleLoadMore = async () => {
    if (!agent?.id || !cursorId || loadingMore) {
      return;
    }

    setLoadingMore(true);

    try {
      const response = await listAgentConversationsPage({
        agentId: agent.id,
        pageSize: PAGE_SIZE,
        cursorId,
      });

      setConversations((previous) => {
        const knownIds = new Set(previous.map((item) => item.id));
        const nextItems = response.items.filter((item) => !knownIds.has(item.id));
        return [...previous, ...nextItems];
      });
      setCursorId(response.nextCursorId);
      setHasMore(response.hasMore);
    } catch (error) {
      console.error('Error loading more agent conversations:', error);
      setSnackbar({
        open: true,
        message: 'Լրացուցիչ զրույցներ բեռնել չհաջողվեց։',
        severity: 'error',
      });
    } finally {
      setLoadingMore(false);
    }
  };

  const NoRowsOverlay = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', p: 3, textAlign: 'center' }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {conversations.length === 0 ? 'Այս ագենտը դեռ զրույց չունի' : 'Զրույց չի գտնվել'}
      </Typography>
      <Typography color="text.secondary" sx={{ maxWidth: 440, lineHeight: 1.7 }}>
        {conversations.length === 0
          ? 'Երբ այս ագենտով զանգեր իրականացվեն, դրանց պատմությունը կհայտնվի այստեղ։'
          : 'Փորձեք փոխել որոնման արտահայտությունը կամ բեռնել լրացուցիչ զրույցներ։'}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mt: 2 }}>
        {query.trim() && (
          <Button variant="outlined" onClick={() => setQuery('')}>
            Մաքրել որոնումը
          </Button>
        )}
        {hasMore && (
          <Button
            variant="contained"
            onClick={handleLoadMore}
            disabled={loadingMore}
            startIcon={loadingMore ? <CircularProgress size={16} color="inherit" /> : null}
          >
            Բեռնել ավելին
          </Button>
        )}
      </Stack>
    </Stack>
  );

  const CustomFooter = () => (
    <GridFooterContainer sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <GridPagination />
      {hasMore && rows.length > 0 && (
        <Button
          variant="contained"
          size="small"
          onClick={handleLoadMore}
          disabled={loadingMore}
          startIcon={loadingMore ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{ ml: 1 }}
        >
          Բեռնել ավելին
        </Button>
      )}
    </GridFooterContainer>
  );

  return (
    <>
      <Stack spacing={3}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 3 },
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            backgroundColor: '#ffffff',
          }}
        >
          <Stack spacing={2.5}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <IconButton
                  onClick={onBack}
                  sx={{
                    bgcolor: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    '&:hover': { bgcolor: '#f1f5f9' },
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    {agent?.name || 'AI ագենտ'}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    Զրույցների ցանկ, ամփոփ տվյալներ և արագ անցում դեպի յուրաքանչյուր զանգի մանրամասները։
                  </Typography>
                </Box>
              </Stack>

              {initialLoadComplete && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Button variant="outlined" startIcon={<EditOutlinedIcon />} onClick={onEditAgent} sx={{ textTransform: 'none', fontWeight: 700 }}>
                    Խմբագրել ագենտը
                  </Button>
                </Stack>
              )}
            </Stack>

            {initialLoadComplete && (
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.25} useFlexGap flexWrap="wrap">
                <Chip icon={<SmartToyOutlinedIcon />} label={agent?.phoneNumberSnapshot?.phoneNumber || 'Համար չկա'} variant="outlined" sx={{ backgroundColor: '#f8fafc' }} />
                <Chip icon={<CallOutlinedIcon />} label={`${conversations.length} զրույց`} variant="outlined" />
                {agent?.updatedAt && (
                  <Chip icon={<AccessTimeOutlinedIcon />} label={`Թարմացվել է ${formatConversationDate(agent.updatedAt)}`} variant="outlined" />
                )}
              </Stack>
            )}
          </Stack>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: 1.75,
            borderRadius: 3,
            border: '1px solid #e2e8f0',
            backgroundColor: '#fbfcff',
          }}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Որոնել համարով կամ վերնագրով…"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
            <Chip
              variant="outlined"
              label={query.trim()
                ? `${rows.length} / ${conversations.length} զրույց`
                : `${conversations.length} զրույց`}
              sx={{ alignSelf: { xs: 'flex-start', sm: 'center' } }}
            />
          </Stack>
        </Paper>

        <Paper elevation={1} sx={{ p: 1.5, borderRadius: 3 }}>
          {(loading || loadingMore) && <LinearProgress sx={{ mb: 1 }} />}

          <Box sx={{ width: '100%', minWidth: 0, minHeight: isXs ? 360 : 460 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading && !initialLoadComplete}
              autoHeight={false}
              disableColumnMenu
              disableRowSelectionOnClick
              density={isXs ? 'compact' : 'standard'}
              rowHeight={isXs ? 84 : 60}
              columnHeaderHeight={56}
              pageSizeOptions={[10, 25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
                sorting: { sortModel: [{ field: 'createdAtMs', sort: 'desc' }] },
              }}
              columnVisibilityModel={columnVisibilityModel}
              localeText={localeText}
              slots={{
                noRowsOverlay: NoRowsOverlay,
                footer: CustomFooter,
              }}
              sx={{
                minHeight: isXs ? 360 : 460,
                '& .MuiDataGrid-columnHeaders': { fontWeight: 700 },
                '& .MuiDataGrid-columnHeaderTitle': {
                  whiteSpace: 'normal',
                  lineHeight: 1.4,
                },
                '& .MuiDataGrid-row:hover': { backgroundColor: 'action.hover' },
                '& .MuiDataGrid-row': { cursor: 'pointer' },
                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus': { outline: 'none' },
              }}
              onRowClick={(params) => onOpenConversation(params.id)}
            />
          </Box>
        </Paper>
      </Stack>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((previous) => ({ ...previous, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AgentConversationListView;
